import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import I18n from 'i18n.js.erb'
import { CURRENCY_EURO, pluralize } from "../../utils/utils";

const LoopPriceEditModal = ({
  showModal,
  products,
  handleConfirm,
  handleCloseModal,
  isLoading,
  error
}) => {
  const [price, setPrice] = useState('');

  const isSingleProduct = products?.length === 1;
  const singleProduct = isSingleProduct ? products[0] : null;

  useEffect(() => {
    if (showModal && products?.length > 0) {
      if (isSingleProduct && singleProduct?.price) {
        const formattedPrice = parseFloat(singleProduct.price).toFixed(2).replace('.', ',');
        setPrice(formattedPrice);
      } else {
        setPrice('');
      }
    }
  }, [showModal, products]);

  const handlePriceChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*[,.]?\d{0,2}$/.test(value)) {
      setPrice(value);
    }
  };

  const parsePrice = (priceString) => {
    if (!priceString) return 0;
    const normalized = priceString.replace(',', '.');
    return parseFloat(normalized);
  };

  const handleSubmit = () => {
    const numericPrice = parsePrice(price);
    if (numericPrice > 0) {
      handleConfirm(products, numericPrice);
    }
  };

  const isValidPrice = price !== '' && parsePrice(price) > 0;

  if (!products || products.length === 0) return null;

  const serialNumbers = products.map(p => p.current_grading?.extended_serial_number || '').filter(Boolean);

  return (
    <Modal
      id="loop-price-edit-modal"
      show={showModal}
      onHide={handleCloseModal}
      animation={false}
      size="md"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100 text-center mt-1">
          {isSingleProduct
            ? I18n.t('js.loopPriceModal.title')
            : pluralize(products.length, 'loopPriceModal.titleBulk')
          }
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="text-center mb-3">
          {isSingleProduct ? (
            <p className="font-size-14px text-muted mb-2">
              {I18n.t('js.loopPriceModal.certificateNumber')}: <b>{serialNumbers[0]}</b>
            </p>
          ) : (
            <p className="font-size-14px text-muted mb-2">
              {pluralize(products.length, 'loopPriceModal.certificatesCount')}: <b>{serialNumbers.join(', ')}</b>
            </p>
          )}
        </div>

        <div className="bg-light p-3 br-8px mb-3">
          <p className="font-size-13px mb-2">
            <b>{I18n.t('js.loopPriceModal.whatIsThis')}</b>
          </p>
          <p className="font-size-12px text-muted mb-0">
            {isSingleProduct
              ? I18n.t('js.loopPriceModal.description')
              : I18n.t('js.loopPriceModal.descriptionBulk')
            }
          </p>
        </div>

        <div className="mb-3">
          <label className="font-size-13px mb-2 d-block">
            <b>{I18n.t('js.loopPriceModal.priceLabel')}</b>
          </label>

          <div className="d-flex flex-nowrap align-items-center input-group">
            <input
              id="loop_price_input"
              type="text"
              inputMode="decimal"
              value={price}
              className="form-control"
              placeholder={I18n.t('js.loopPriceModal.pricePlaceholder')}
              onChange={handlePriceChange}
              autoFocus
            />
            <div className="input-group-append">
              <span className="input-group-text bg-intense-blue-imp text-white border-none">
                {CURRENCY_EURO}
              </span>
            </div>
          </div>
        </div>

        <div className="alert alert-info font-size-12px mb-0">
          <i className="fas fa-info-circle mr-2"></i>
          {isSingleProduct
            ? I18n.t('js.loopPriceModal.note')
            : I18n.t('js.loopPriceModal.noteBulk')
          }
        </div>

        {error && (
          <div className="alert alert-danger font-size-12px mt-3 mb-0">
            <i className="fas fa-exclamation-circle mr-2"></i>
            {I18n.t('js.loopPriceModal.errorMessage')}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn-standard-outline br-20px pr-2 pl-2 w-120px btn-standard-outline-resized text-weight-500"
          onClick={handleCloseModal}
          disabled={isLoading}
        >
          {I18n.t('js.common.button.cancel').toUpperCase()}
        </button>

        <button
          type="button"
          className="btn-standard bg-aig-gradient text-light br-20px px-3 btn-standard-resized pt-6px-imp h-35px text-weight-500"
          disabled={!isValidPrice || isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          ) : (
            I18n.t('js.common.button.confirm').toUpperCase()
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default LoopPriceEditModal;
